import PropTypes from "prop-types";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import { IconChevronRight } from "@tabler/icons";
import { Link as RouterLink } from "react-router-dom";

/**
 * The trail back to the country you came from.
 *
 * The screens reachable from a country profile — prices, questions, documents,
 * gallery, awards — are all deep links with composite ids in the URL and no
 * heading that says which country they belong to. Landing on one of them, the
 * only way back was the browser button, and the only clue you were on the
 * right country was the row data itself.
 *
 * Not the template's Breadcrumb component: that is a full-width banner with a
 * background image, built to be a page header. This is a line of text.
 *
 * Items are `{ label, to }`; the last one is rendered as plain text, and any
 * null entry is dropped so a caller can write a conditional link inline
 * without building the array in two steps.
 */
const Trail = ({ items }) => {
  const trail = (items ?? []).filter(Boolean);
  if (trail.length === 0) return null;

  return (
    <Breadcrumbs
      separator={<IconChevronRight size={14} />}
      aria-label="breadcrumb"
      sx={{ mb: 2 }}
    >
      {trail.map((item, index) =>
        item.to && index < trail.length - 1 ? (
          <Link
            key={item.label}
            component={RouterLink}
            to={item.to}
            state={item.state}
            underline="hover"
            color="inherit"
            variant="body2"
          >
            {item.label}
          </Link>
        ) : (
          <Typography key={item.label} variant="body2" color="text.primary">
            {item.label}
          </Typography>
        )
      )}
    </Breadcrumbs>
  );
};

Trail.propTypes = {
  items: PropTypes.array.isRequired,
};

/**
 * The standard country trail. `country` is whatever the screen was handed in
 * navigation state, which is not always a full country row — arriving the old
 * way (visa type details, then a nationality) hands over the nationality
 * instead, with no country id. Without an id the country step is dropped
 * rather than rendered as a dead link.
 */
export const countryTrail = (country, current, countryId) => {
  const id = countryId ?? country?.id;
  return [
    { label: "All countries", to: "/countries" },
    id
      ? { label: country?.name ?? "Country", to: `/countries/${id}/profile` }
      : null,
    { label: current },
  ];
};

export default Trail;
